import {
  useSignalR,
  type SignalRReceiveEvent,
  type SubscribeToEventOptions
} from '@gat-ship/lib/signalr'
import { ref } from 'vue'
import type {
  AgencyAddedSignalRPayload,
  AgencyMovedSignalRPayload,
  AgencyDeactivatedSignalRPayload,
  AgencyReactivatedSignalRPayload,
  AgencyNameChangedSignalRPayload,
  AgencyContactInfoChangedSignalRPayload,
  AgencyLinkedToNewCompanySignalRPayload,
  AgencyGroupAddedSignalRPayload,
  AgencyGroupMovedSignalRPayload,
  AgencyGroupNameChangedSignalRPayload,
  AgencyGroupDeactivatedSignalRPayload
} from '@gat-ship/types/signal-r-events'
import { signalREvent } from '@gat-ship/types/signal-r-events'

export type AgencyEventHandlers = {
  onAgencyAdded?: (payload: SignalRReceiveEvent<AgencyAddedSignalRPayload>) => void
  onAgencyMoved?: (payload: SignalRReceiveEvent<AgencyMovedSignalRPayload>) => void
  onAgencyDeactivated?: (payload: SignalRReceiveEvent<AgencyDeactivatedSignalRPayload>) => void
  onAgencyReactivated?: (payload: SignalRReceiveEvent<AgencyReactivatedSignalRPayload>) => void
  onAgencyNameChanged?: (payload: SignalRReceiveEvent<AgencyNameChangedSignalRPayload>) => void
  onAgencyContactInfoChanged?: (
    payload: SignalRReceiveEvent<AgencyContactInfoChangedSignalRPayload>
  ) => void
  onAgencyLinkedToNewCompany?: (
    payload: SignalRReceiveEvent<AgencyLinkedToNewCompanySignalRPayload>
  ) => void
  onAgencyGroupAdded?: (payload: SignalRReceiveEvent<AgencyGroupAddedSignalRPayload>) => void
  onAgencyGroupMoved?: (payload: SignalRReceiveEvent<AgencyGroupMovedSignalRPayload>) => void
  onAgencyGroupNameChanged?: (
    payload: SignalRReceiveEvent<AgencyGroupNameChangedSignalRPayload>
  ) => void
  onAgencyGroupDeactivated?: (
    payload: SignalRReceiveEvent<AgencyGroupDeactivatedSignalRPayload>
  ) => void
}

export function useAgencySignalREvents(handlers?: AgencyEventHandlers) {
  const { subscribe } = useSignalR()

  const subscription = ref<() => void>()

  function subscribeToAgencyAdded(
    cb: (payload: SignalRReceiveEvent<AgencyAddedSignalRPayload>) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<AgencyAddedSignalRPayload>(
      signalREvent.Organization.V1.AgencyAdded,
      cb,
      options
    )
  }

  function subscribeToAgencyMoved(
    cb: (payload: SignalRReceiveEvent<AgencyMovedSignalRPayload>) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<AgencyMovedSignalRPayload>(
      signalREvent.Organization.V1.AgencyMoved,
      cb,
      options
    )
  }

  function subscribeToAgencyDeactivated(
    cb: (payload: SignalRReceiveEvent<AgencyDeactivatedSignalRPayload>) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<AgencyDeactivatedSignalRPayload>(
      signalREvent.Organization.V1.AgencyDeactivated,
      cb,
      options
    )
  }

  function subscribeToAgencyReactivated(
    cb: (payload: SignalRReceiveEvent<AgencyReactivatedSignalRPayload>) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<AgencyReactivatedSignalRPayload>(
      signalREvent.Organization.V1.AgencyReactivated,
      cb,
      options
    )
  }

  function subscribeToAgencyNameChanged(
    cb: (payload: SignalRReceiveEvent<AgencyNameChangedSignalRPayload>) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<AgencyNameChangedSignalRPayload>(
      signalREvent.Organization.V1.AgencyNameChanged,
      cb,
      options
    )
  }

  function subscribeToAgencyContactInfoChanged(
    cb: (payload: SignalRReceiveEvent<AgencyContactInfoChangedSignalRPayload>) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<AgencyContactInfoChangedSignalRPayload>(
      signalREvent.Organization.V1.AgencyContactInfoChanged,
      cb,
      options
    )
  }

  function subscribeToAgencyLinkedToNewCompany(
    cb: (payload: SignalRReceiveEvent<AgencyLinkedToNewCompanySignalRPayload>) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<AgencyLinkedToNewCompanySignalRPayload>(
      signalREvent.Organization.V1.AgencyLinkedToNewCompany,
      cb,
      options
    )
  }

  function subscribeToAgencyGroupAdded(
    cb: (payload: SignalRReceiveEvent<AgencyGroupAddedSignalRPayload>) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<AgencyGroupAddedSignalRPayload>(
      signalREvent.Organization.V1.AgencyGroupAdded,
      cb,
      options
    )
  }

  function subscribeToAgencyGroupMoved(
    cb: (payload: SignalRReceiveEvent<AgencyGroupMovedSignalRPayload>) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<AgencyGroupMovedSignalRPayload>(
      signalREvent.Organization.V1.AgencyGroupMoved,
      cb,
      options
    )
  }

  function subscribeToAgencyGroupNameChanged(
    cb: (payload: SignalRReceiveEvent<AgencyGroupNameChangedSignalRPayload>) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<AgencyGroupNameChangedSignalRPayload>(
      signalREvent.Organization.V1.AgencyGroupNameChanged,
      cb,
      options
    )
  }

  function subscribeToAgencyGroupDeactivated(
    cb: (payload: SignalRReceiveEvent<AgencyGroupDeactivatedSignalRPayload>) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<AgencyGroupDeactivatedSignalRPayload>(
      signalREvent.Organization.V1.AgencyGroupRemoved,
      cb,
      options
    )
  }

  function unsubscribeFromAgencyEvents() {
    subscription.value?.()
    subscription.value = undefined
  }

  function subscribeToAgencyEvents() {
    if (subscription.value) {
      return subscription.value
    }

    if (handlers) {
      const unsubscribeFunctions: (() => void)[] = []

      // Subscribe only to events with defined handlers
      if (handlers.onAgencyAdded) {
        unsubscribeFunctions.push(subscribeToAgencyAdded(handlers.onAgencyAdded))
      }
      if (handlers.onAgencyMoved) {
        unsubscribeFunctions.push(subscribeToAgencyMoved(handlers.onAgencyMoved))
      }
      if (handlers.onAgencyDeactivated) {
        unsubscribeFunctions.push(subscribeToAgencyDeactivated(handlers.onAgencyDeactivated))
      }
      if (handlers.onAgencyReactivated) {
        unsubscribeFunctions.push(subscribeToAgencyReactivated(handlers.onAgencyReactivated))
      }
      if (handlers.onAgencyNameChanged) {
        unsubscribeFunctions.push(subscribeToAgencyNameChanged(handlers.onAgencyNameChanged))
      }
      if (handlers.onAgencyContactInfoChanged) {
        unsubscribeFunctions.push(
          subscribeToAgencyContactInfoChanged(handlers.onAgencyContactInfoChanged)
        )
      }
      if (handlers.onAgencyLinkedToNewCompany) {
        unsubscribeFunctions.push(
          subscribeToAgencyLinkedToNewCompany(handlers.onAgencyLinkedToNewCompany)
        )
      }
      if (handlers.onAgencyGroupAdded) {
        unsubscribeFunctions.push(subscribeToAgencyGroupAdded(handlers.onAgencyGroupAdded))
      }
      if (handlers.onAgencyGroupMoved) {
        unsubscribeFunctions.push(subscribeToAgencyGroupMoved(handlers.onAgencyGroupMoved))
      }
      if (handlers.onAgencyGroupNameChanged) {
        unsubscribeFunctions.push(
          subscribeToAgencyGroupNameChanged(handlers.onAgencyGroupNameChanged)
        )
      }
      if (handlers.onAgencyGroupDeactivated) {
        unsubscribeFunctions.push(
          subscribeToAgencyGroupDeactivated(handlers.onAgencyGroupDeactivated)
        )
      }

      subscription.value = () => {
        unsubscribeFunctions.forEach((unsubscribe) => unsubscribe())
      }
    }

    return subscription.value
  }

  return {
    subscribeToAgencyEvents,
    unsubscribeFromAgencyEvents
  }
}
